import {
  AnswerThemesList_AnalysisThemeFragment,
  StakeholderStats_StakeholderAnalysisThemeStatsFragment,
  SurveyQuestionType,
  SurveyThemeQualiQuestion_AnswerThemeFragment,
  useAnalysisThemeModalQuery,
} from '@/graphql/generated';
import clsx from 'clsx';
import { getThemeBgColor } from './AnalysisThemeTag';
import { LoaderFullscreen } from '@/components/layout/Loader';
import {
  getSurveyTargetLabel,
  sanitizeTypeformString,
} from '@/services/SurveyService';
import { PieChart } from 'react-minimal-pie-chart';
import { EditableAnalysisThemeTitle } from './EditableAnalysisThemeTitle';
import { useProjectContext } from '@/providers/ProjectContextProvider';
import { useCurrentUser } from '@/providers/CurrentUserProvider';
import { ExportableToImage } from '@/components/generic/export/ExportableToImage';
import { MessageBox, MessageBoxType } from '@/components/layout/MessageBox';
import { QuoteWhiteIcon } from '@/components/icons';
import { useTranslation } from '@/hooks/useTranslation';
import { useModal } from '@/components/modals/Modal';

export function AnalysisThemeModal({
  analysisThemeId,
}: {
  analysisThemeId: string;
}) {
  const { t } = useTranslation();
  const query = useAnalysisThemeModalQuery({
    variables: {
      id: analysisThemeId,
    },
    fetchPolicy: 'network-only',
  });

  if (query.loading) {
    return <LoaderFullscreen />;
  }

  if (query.error) {
    return (
      <MessageBox type={MessageBoxType.Error}>{query.error.message}</MessageBox>
    );
  }

  if (!query.data?.analysisTheme) {
    return null;
  }

  const analysisTheme = query.data.analysisTheme;
  const answerThemeList = analysisTheme.answers || [];

  return (
    <div className="flex flex-col w-full">
      <div
        className={clsx(
          'py-8 px-16 border-y border-gray-100 flex flex-col gap-4',
          getThemeBgColor(analysisTheme.color, false, null),
        )}
      >
        <EditableAnalysisThemeTitle theme={analysisTheme} />

        <div className="grid grid-cols-3 gap-4">
          <StakeholderStats
            stakeholderAnalysisThemeStats={
              analysisTheme.ceoStakeholderRepartition
            }
          />
          <StakeholderStats
            stakeholderAnalysisThemeStats={
              analysisTheme.internalStakeholderRepartition
            }
          />
          <StakeholderStats
            stakeholderAnalysisThemeStats={
              analysisTheme.externalStakeholderRepartition
            }
          />
        </div>
      </div>
      <div className="w-full grow overflow-scroll p-16">
        <h2 className="title-h6 flex items-center gap-2 mb-4">
          <span>{t('analysis_themes.modal.content')}</span>
          <span className="tag gray">{answerThemeList.length}</span>
        </h2>
        <AnswerThemesList analysisTheme={analysisTheme} />
      </div>
    </div>
  );
}

function StakeholderStats({
  stakeholderAnalysisThemeStats,
}: {
  stakeholderAnalysisThemeStats: StakeholderStats_StakeholderAnalysisThemeStatsFragment;
}) {
  const { t } = useTranslation();
  return (
    <div className="bg-white rounded-xl p-4 shadow-sm flex items-center gap-2 justify-between sami-body-small">
      <div className="flex flex-col items-start gap-0">
        <span className="text-gray-500 font-bold">
          {getSurveyTargetLabel(stakeholderAnalysisThemeStats?.surveyTarget)}
        </span>
        <span className="text-gray-500 mt-1">
          {stakeholderAnalysisThemeStats.themeUniqueMentions}/
          {stakeholderAnalysisThemeStats.totalRespondents}{' '}
          {t('analysis_themes.modal.respondents', {
            count: stakeholderAnalysisThemeStats.totalRespondents,
          })}
        </span>
        <span className="text-gray-500">
          {stakeholderAnalysisThemeStats.themeMentions}{' '}
          {t('analysis_themes.modal.mentions', {
            count: stakeholderAnalysisThemeStats.themeMentions,
          })}
        </span>
      </div>
      <div className="flex flex-col items-center">
        <div className="w-6 h-6">
          <PieChart
            data={[
              {
                value: stakeholderAnalysisThemeStats.themeUniqueMentions,
                color: '#282a2c',
              },
              {
                value:
                  stakeholderAnalysisThemeStats.totalRespondents -
                  stakeholderAnalysisThemeStats.themeUniqueMentions,
                color: '#E4E6F2',
              },
            ]}
            startAngle={270}
            lineWidth={43}
          />
        </div>
        {stakeholderAnalysisThemeStats.totalRespondents > 0 ? (
          <span className="sami-body-bold">
            {Math.round(
              (100 * stakeholderAnalysisThemeStats.themeUniqueMentions) /
                stakeholderAnalysisThemeStats.totalRespondents,
            )}
            %
          </span>
        ) : (
          <span className="sami-body-bold">-%</span>
        )}
      </div>
    </div>
  );
}

function AnswerThemesList({
  analysisTheme,
}: {
  analysisTheme: AnswerThemesList_AnalysisThemeFragment;
}) {
  return (
    <div className="columns-2 h-full">
      {analysisTheme.answers?.map((answerTheme) => {
        if (answerTheme?.question) {
          const isQualiQuestion = [
            SurveyQuestionType.LongText,
            SurveyQuestionType.ShortText,
            SurveyQuestionType.Email,
          ].includes(answerTheme.question.type);
          if (isQualiQuestion) {
            return (
              <SurveyThemeQualiQuestion
                key={answerTheme.id}
                answerTheme={answerTheme}
              />
            );
          } else {
            // Not supported anymore
            return null;
          }
        }

        return null;
      })}
    </div>
  );
}

function SurveyThemeQualiQuestion({
  answerTheme,
}: {
  answerTheme: SurveyThemeQualiQuestion_AnswerThemeFragment;
}) {
  const projectContext = useProjectContext();
  const { currentUser } = useCurrentUser();

  const title = answerTheme.question?.title || 'Question';
  return (
    <ExportableToImage name="Verbatim">
      <div className="border border-gray-100 rounded-xl mb-4 break-inside-avoid max-w-xl">
        <div className="p-4 sami-body-small-bold uppercase text-gray-500">
          {sanitizeTypeformString(title, projectContext, currentUser)}
        </div>
        <div className="p-4 bg-yellow-50 rounded-b-xl sami-heading-small">
          <div className="">
            <QuoteWhiteIcon className="w-5 h-5" />
          </div>
          <div className="py-4 flex items-center justify-center text-center">
            {answerTheme.chunk?.content}
          </div>
        </div>
      </div>
    </ExportableToImage>
  );
}

export const useAnalysisThemeModal = () => {
  const modal = useModal();
  const { t } = useTranslation();

  const openThemeModal = (id: string) => {
    modal.openModal(<AnalysisThemeModal analysisThemeId={id} />, {
      title: t('analysis_themes.modal.title'),
      widthClass: 'w-8/12',
      padding: 'p-0',
      isFullscreen: true,
      displayTitleBar: true,
    });
  };

  return {
    openThemeModal,
  };
};
